import './create-report-search-featured-card.scss'

import _ from 'lodash'
import { get, set } from 'lodash'
import { first, switchMap } from 'rxjs/operators'
import { PropType } from 'vue'
import { Watch } from 'vue-property-decorator'
import Component from 'vue-class-component'
import { faCamera, faEdit, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { ConditionReportState, Media, SearchFeatured } from '@skinsoft/types'
import { conditionReportSelectors } from '@skinsoft/selectors'
import { ConditionReportDataStore } from '@skinsoft/entity/src/condition-report'
import { ItemDataStore, itemTypeFormatters } from '@skinsoft/entity/src/item'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus'
import { SearchFeaturedCard, SEARCH_FEATURED_CARD_TITLE_CLICKED, TitleClickAction, ActionType } from '@skinsoft/ui'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'

interface LoadingButton {
    type: string,
    isLoading: boolean,
}

@Component({
    components: {
        sSearchFeaturedCard: SearchFeaturedCard,
    },
    props: {
        searchFeatured: {},
        rounded: Boolean,
        targetRouteName: String,
        displayedButtons: Array,
        displayType: {
            type: Boolean,
            default: false
        },
        titleClickAction: Object as PropType<TitleClickAction>,
    },
    template: require('./create-report-search-featured-card.html'),
})
export class CreateReportSearchFeaturedCard extends ReactiveVue {
    public searchFeatured: SearchFeatured
    public targetRouteName: string
    private displayedButtons: any[]
    private displayType: boolean
    private titleClickAction: TitleClickAction

    public hasLastReport: boolean = false
    private lastReportStatus: string = ''
    private lastReportId: string = ''

    private loadingButtons: LoadingButton[] = []

    private $itemDataStore: ItemDataStore
    private $conditionReportDataStore: ConditionReportDataStore
    private $imageTransferHelper: ImageTransferHelper

    private loadingIcon = faCircleNotch

    private SEARCH_FEATURED_CARD_TITLE_CLICKED = SEARCH_FEATURED_CARD_TITLE_CLICKED

    mounted() {
        this.loadingButtons = this.displayedButtons.map(button => {
            return {
                type: button.type,
                isLoading: false,
            }
        })
    }

    public async createNewReport(fromLast: boolean = false) {
        const newReportId = fromLast
            ? await this.duplicateReportAndGetId(this.searchFeatured.lastConditionReportId)
            : await this.createReportAndGetId()

        this.$router.push({
            name: this.targetRouteName,
            params: {
                reportId: newReportId,
            },
        })
    }

    public async createReportAndGetId(): Promise<string> {
        const report = await this.$conditionReportDataStore.create().pipe(first()).toPromise()

        // Setting concerned item - temporary, TODO use right field when available
        report.conditionReportCS.comments = []
        set(report, 'conditionReportCS.comments', [])
        set(report, 'conditionReportCS.linkableItem', {
            linkableItem: this.searchFeatured.itemId,
            type: this.searchFeatured.itemType,
        })
        await this.$conditionReportDataStore.update(report.id, report).pipe(first()).toPromise()

        return report.id
    }

    public async duplicateReportAndGetId(reportToDuplicateId: string): Promise<string> {
        const newReport = await this.$conditionReportDataStore
            .duplicate(reportToDuplicateId)
            .pipe(first())
            .toPromise()
        return newReport.id
    }

    public checkLastReport() {
        this.lastReportId = get(this.searchFeatured, 'lastConditionReportId')
        if (this.lastReportId) {
            this.$conditionReportDataStore.fetchOne(this.lastReportId).subscribe((conditionReport) => {
                const isArchived: boolean = get(conditionReport, 'managementData.archived')
                this.lastReportStatus = conditionReportSelectors.workflowState(conditionReport)
                this.hasLastReport = !isArchived
            })
        } else {
            this.hasLastReport = false
        }
    }

    @Watch('searchFeatured')
    private onSearchFeaturedChanged() {
        if (this.$conditionReportDataStore !== undefined) {
            this.checkLastReport()
        }
    }

    private performAction(button: any) {
        switch (button.type) {
            case 'camera':
                let fileInputElement = this.$refs.camera[0]
                if (fileInputElement instanceof HTMLElement) {
                    fileInputElement.click()
                }
                break
            case 'modifyItemRecord':
                this.$router.push({
                    name: this.targetRouteName,
                    params: { itemId: this.searchFeatured.itemId },
                    query: { imageChoice: 'false' },
                })
                break
            case 'createNewReport':
                this.createNewReport()
                break
            case 'createNewReportFromLast':
                this.createNewReport(true)
                break
            case 'consultLastReport':
                this.$router.push({
                    name: button.route,
                    params: { reportId: this.lastReportId }
                })
        }
    }

    private onTitleClicked() {
        if(this.titleClickAction && this.titleClickAction.action === ActionType.EDIT){
            this.$router.push({name: this.titleClickAction.targetRouteName, params: {itemId: this.searchFeatured.itemId}, query: {imageChoice: 'false'}})
        }
    }

    public onFileUploaded(event) {
        this.toggleButtonLoading('camera')

        const myNewFile = new File(
            [event.target.files[0]], 
            "REP-"+Date.now()+"."+ event.target.files[0].name.split('.').pop(), // we need to provide a name to create file, but we don't reuse that name
            {type: event.target.files[0].type}
        )
        const reader = new FileReader();
        reader.readAsDataURL(myNewFile);

        reader.addEventListener("load", async () => {
            const media = { url: reader.result, file: myNewFile };
            if(media.url == null) {
                media.url = '';
            }else if(media.url instanceof ArrayBuffer){
                media.url = media.url.toString();
            }
            
            const { cacheKey, fileName } = await this.$imageTransferHelper.uploadImage(media.file.type, media.file,  media.url);
            let mediaIdToSave = cacheKey
            
            let uploadedMedia: Media | null = null
            if(navigator.onLine) {
                try {
                    uploadedMedia = await this.$imageTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(fileName, 15, 1000)
                } catch(err) {

                    const alertPayload = {
                        message: this.$t('alerts.medias.failGetIdFromFileName'),
                        variant: "danger"
                    };
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                    this.toggleButtonLoading('camera')
                    return
                }

                if(uploadedMedia?.id) {
                    mediaIdToSave = uploadedMedia.id
                    this.$imageTransferHelper.addImageToReporterSubAlbum(uploadedMedia.id).subscribe()
                }
            }
            
            this.$itemDataStore.fetchOne(this.searchFeatured.itemId).pipe(
                switchMap(item => {
                    if(_.get(item, 'associatedMedias.medias') === null){
                        _.set(item, 'associatedMedias.medias', [mediaIdToSave]);
                    }else{
                        _.get(item, 'associatedMedias.medias').push(mediaIdToSave);
                    }

                    if(MediaTransferHelper.isLocalMedia(mediaIdToSave)) {
                        return this.$itemDataStore.updateOfflineMode(item.id, item)
                    } else {
                        return this.$itemDataStore.update(item.id, item)
                    }
                }),
            ).subscribe((updatedItem) => {
                this.performAction({type: "modifyItemRecord"})
            })
        }, false);
    }

    private getIcon(iconName: string) {
        if(this.buttonIsLoading(iconName)) {
            return this.loadingIcon
        }

        switch (iconName) {
            case 'camera':
                return faCamera
            case 'edit':
                return faEdit
        }
    }
    
    private buttonIsLoading(iconName: string) {
        const loadingButton = this.loadingButtons.find(btn => btn.type === iconName)
        if(loadingButton?.isLoading) {
            return true
        }
        return false
    }

    private toggleButtonLoading(iconName: string) {
        const cameraIconIndex = this.loadingButtons.findIndex(btn => btn.type == iconName)
        if(cameraIconIndex > -1) {
            const buttonStatus = this.loadingButtons[cameraIconIndex]

            if(buttonStatus) {
                this.loadingButtons.splice(cameraIconIndex, 1, {
                    type: buttonStatus.type,
                    isLoading: !buttonStatus.isLoading,
                })
            }
        }
    }

    private isDisplayed(button: any) {
        switch (button.condition) {
            case undefined:
                return true
            case 'hasLastReportNotArchived':
                return this.hasLastReport
            case 'hasLastReportClosed':
                return this.lastReportId && (this.lastReportStatus == ConditionReportState.CLOSED)
        }
    }

    private get readableCardType() {
        return this.$t(itemTypeFormatters.getReadableType(this.searchFeatured.itemType))
    }
}
